import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import {
    SigninUserResponse,
    SigninUserResponseResponseResult,
    UserProfileViewModel,
} from '../../proxy/proxies';
import { getStoredEnterpriseId } from './storage/enterprise-storage';
import { LocalStorageService } from './storage/local-storage.service';
import { AppConfigService } from './app-config.service';
import { PermissionService } from './permission.service';
import { CropMapAccessService } from './cropmap-access.service';
import { UserService } from './user.service';
import { PlantationService } from '../../features/plantation/plantation.service';
import { DateTime } from 'luxon';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    userInfo: UserProfileViewModel | null = null;
    private currentUserSubject = new BehaviorSubject<UserProfileViewModel | null>(null);
    currentUser$ = this.currentUserSubject.asObservable();

    private userSessionSubject = new BehaviorSubject<SigninUserResponse | null>(null);

    private readonly router = inject(Router);
    private readonly _localStorageService = inject(LocalStorageService);
    private readonly _appConfigService = inject(AppConfigService);
    private readonly _permissionService = inject(PermissionService);
    private readonly _cropMapAccessService = inject(CropMapAccessService);
    private readonly _injector = inject(Injector);

    handleSigninResponse(response: SigninUserResponseResponseResult): void {
        // Store in local storage
        this._localStorageService.setItem('userId', response.result.id);
        this._localStorageService.setItem('token', response.result.token);
        this._localStorageService.setItem('tokenExpiration', response.result.validUntil?.toString() ?? '');

        this._cropMapAccessService.resetAll();

        // Update session state - this will trigger notification polling
        this.userSessionSubject.next(response.result);

        // Initialize authenticated configurations after successful login
        this.initializeAuthenticatedConfigurations();
    }

    private async initializeAuthenticatedConfigurations(): Promise<void> {
        try {
            // Add a small delay to ensure the auth token is properly set in interceptors
            setTimeout(async () => {
                try {
                    const success = await this._appConfigService.initializeAuthenticatedConfigurations();
                    if (!success) {
                        console.warn(
                            'Failed to initialize authenticated configurations. Please check backend configuration.'
                        );
                    }
                } catch (error) {
                    console.warn('Failed to initialize authenticated configurations:', error);
                }
            }, 500); // Give time for token to be properly set
        } catch (error) {
            console.warn('Error setting up authenticated configurations initialization:', error);
        }
    }

    setCurrentUser(user: UserProfileViewModel) {
        this.userInfo = user;
        this.applyEnterpriseDisplayName();
        this.currentUserSubject.next(this.userInfo);
    }

    /**
     * Sets the current user from the user profile.
     */
    setCurrentUserFromUserProfile(profile: UserProfileViewModel): void {
        this.setCurrentUser(profile);
    }

    /**
     * Refreshes client-only `enterpriseDisplayName` on {@link userInfo} after `selectedEnterpriseId` in storage
     * or the CropMap enterprise list is known (e.g. features resolver).
     */
    setEnterpriseDisplayNameFromContext(explicitName?: string | undefined): void {
        if (!this.userInfo) return;
        this.applyEnterpriseDisplayName(explicitName);
        this.currentUserSubject.next(this.userInfo);
    }

    private applyEnterpriseDisplayName(explicitName?: string | undefined): void {
        const user = this.userInfo;
        if (!user) return;
        const u = user as UserProfileViewModel & { enterpriseDisplayName?: string };
        delete u.enterpriseDisplayName;
        const enterpriseId = getStoredEnterpriseId(this._localStorageService);
        const fromProfile =
            (enterpriseId && user.enterprises?.find((e) => e.id === enterpriseId)?.name?.trim()) ||
            undefined;
        const name = explicitName?.trim() || fromProfile;
        if (name) {
            u.enterpriseDisplayName = name;
        }
    }

    /**
     * Plantation and UserService are resolved lazily (not field-injected) to avoid DI cycles:
     * UserService → AuthService, PlantationService → UserService.
     */
    resetPlantationOnLogout(): void {
        this._injector.get(PlantationService).resetOnLogout();
    }

    /**
     * We do not use `inject(UserService)` on this class (field or constructor).
     * UserService already uses `inject(AuthService)`. If AuthService also injected UserService
     * at construction time, Angular would try to create both at once and neither could finish
     * first. `this._injector.get(...)` looks up UserService only when logout runs.
     */
    logOut(): void {
        this.resetPlantationOnLogout();
        this._injector.get(UserService).performLogout();
    }

    /** Clears persisted auth session (storage, access, permissions, subjects). */
    finishLogout(): void {
        const rememberMe = this._localStorageService.getItem<{ email: string; password: string }>('rememberMe');
        const lang = this._localStorageService.getItem<string>('lang');
        const languageGuid = this._localStorageService.getItem<string>('languageGuid');
        const languageISO1 = this._localStorageService.getItem<string>('languageISO1');

        this._localStorageService.clear();
        sessionStorage.clear();

        if (rememberMe) this._localStorageService.setItem('rememberMe', rememberMe);
        if (lang) this._localStorageService.setItem('lang', lang);
        if (languageGuid) this._localStorageService.setItem('languageGuid', languageGuid);
        if (languageISO1) this._localStorageService.setItem('languageISO1', languageISO1);

        this._appConfigService.clearAuthenticatedConfigurations();
        this._cropMapAccessService.clear();
        this._permissionService.clearPermissions();

        this.userInfo = null;
        this.currentUserSubject.next(null);
        this.userSessionSubject.next(null);
    }

    navigateToLogin(): void {
        this.router.navigateByUrl('/auth/login');
    }

    /**
     * Restores the session from localStorage on app refresh.
     * Returns true if a valid session was restored.
     */
    restoreSession(): boolean {
        if (!this.isAuthenticated()) return false;

        const token = this._localStorageService.getItem<string>('token');
        const tokenExpiration = this._localStorageService.getItem<string>('tokenExpiration');
        const userId = this._localStorageService.getItem<string>('userId');

        if (!token || !userId) return false;

        this.userSessionSubject.next({
            id: userId,
            token,
            validUntil: tokenExpiration ? DateTime.fromISO(tokenExpiration) : undefined,
        } as SigninUserResponse);

        return true;
    }

    isAuthenticated(): boolean {
        const token = this._localStorageService.getItem('token');
        const tokenExpiration = this._localStorageService.getItem('tokenExpiration');
        if (!token || !tokenExpiration) return false;

        const expirationDate = DateTime.fromISO(tokenExpiration as string);
        return !!token && expirationDate > DateTime.now();
    }

    getTokenExpirationDateTime(): DateTime {
        const tokenExpiration = this._localStorageService.getItem('tokenExpiration');
        return DateTime.fromISO(tokenExpiration as string);
    }

    clearAuthTokens(): void {
        this._localStorageService.removeItem('userId');
        this._localStorageService.removeItem('token');
        this._localStorageService.removeItem('tokenExpiration');
        this._cropMapAccessService.clear();
        this._permissionService.clearPermissions();
    }
}
